import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApiTrigger } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { ContactVendorRequest } from "../../../../../../common/service/server/dto/UpdateVendorRequest";
import { Error as RequestsError } from "../../../../../copy/requests";
import { usePrimaryOrganization } from "../../../../../hooks";
import { useContactVendorsMessageTemplate } from "../../../../../hooks/useTemplates";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { VendorEmailDisplayDialog } from "../../../../dialog/VendorEmailDisplayDialog";
import { EmailButton } from "../../../../organisms/EmailButton";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import {
  BUSINESS_SURVEY_NAME,
  KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG,
} from "../../../../../surveys/businessSurvey";
import {
  isRequestContextApplicableWithCrunchFiltering,
  isRequestRegionApplicable,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { collectedPICsFromFilteredCollection } from "../../../../../../common/service/resources/data-map/dataMapTransformers";
import { DataRequestDetail, OrganizationVendor } from "../../../../../../common/models";
import {
  CategoryDto,
  DataMapDto,
} from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { Tooltip } from "@mui/material";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { getSensitivePICsWithFilters } from "../../../../../../common/service/resources/data-map/picHelpers";
import { getAllInformationForLimitRequests } from "../../../../../pages/requestInstructions/LimitInstructionsPage";
import { isEmptyDataMap } from "../../../../../../common/service/resources/data-map/dataMapHelpers";
import { Routes } from "../../../../../root/routes";
import { Organization } from "../../../../../../common/models";
import { isConnectedSystemVendor } from "../../../../../util/vendorNesting";

/**
 * Returns a list of all PIC IDs that are collected from collection groups that have the Sensitive Inferences PIC.
 * Note: as of May 2025, this is now a legacy way of determining limit vendors
 * @deprecated Use `getAllInformationForLimitRequests` instead
 */
export const getCollectedPicsFromCollectionGroupsWithSensitiveInferencesPIC = (
  dataMap: DataMapDto,
  isEmployee: boolean,
) => {
  return collectedPICsFromFilteredCollection(dataMap, {
    onlyIncludeSensitiveInferences: true,
    collectionGroupType: isEmployee ? "EMPLOYMENT" : undefined,
    exclude: { collectionGroupType: isEmployee ? undefined : "EMPLOYMENT" },
  }).map((pic) => pic.id);
};

/**
 * Filters the PICs in `collectedPICIds` to only include PICs that are sensitive in CA, along with the "Inferences based on sensitive information" PIC.
 * Note: as of May 2025, this is now a legacy way of determining limit vendors
 * @deprecated Use `getAllInformationForLimitRequests` instead
 */
export const filterSensitivePICs = (dataMap: DataMapDto, collectedPICIds: string[]) => {
  return (
    getSensitivePICsWithFilters(dataMap, {
      filterPicIds: collectedPICIds,
      // Note: We want to include this since it triggers the Limit Form but technically its not a sensitive PIC
      includeInferencesBasedOnSensitiveInformationPIC: true,
      mustBeSensitiveInCA: true,
    }).sort((a, b) => a.picGroupOrder - b.picGroupOrder || a.order - b.order) ?? []
  );
};

/**
 * This only includes vendors that have the "Inferences based on Sensitve Information" PIC disclosure
 * Note: as of May 2025, this is now a legacy way of determining limit vendors
 * @deprecated Use `getAllInformationForLimitRequests` instead
 */
export const filterLimitVendors = (
  vendors: OrganizationVendor[],
  dataMap: DataMapDto,
  answers: SurveyAnswers,
  context?: CollectionContext,
) => {
  const sensitiveInferencesId = Object.values(dataMap.categories).find(
    (cat) => cat.key === "inferences-based-on-sensitive-information",
  )?.id;

  const sensitiveInformationInferenceVendors = vendors.filter((v) => {
    const vendorSensitiveInferenceDisclosures = dataMap.disclosure.filter(
      (d) => d.vendorId === v.vendorId && d.picId === sensitiveInferencesId,
    );

    // Make sure that at least one collection group matches the context (if provided)
    return vendorSensitiveInferenceDisclosures.some((d) => {
      if (!context) {
        return true;
      }

      let collectionGroup = dataMap.collectionGroups[d.groupId];
      if (!collectionGroup) {
        return false;
      }

      return (
        (context === "CONSUMER" &&
          (collectionGroup.type === "BUSINESS_TO_BUSINESS" ||
            collectionGroup.type === "BUSINESS_TO_CONSUMER")) ||
        (context === "EMPLOYEE" && collectionGroup.type === "EMPLOYMENT")
      );
    });
  });

  return sensitiveInformationInferenceVendors;
};

/**
 * Returns subset of vendors that should be contacted for a limit request (including vendors that may have already been contacted).
 */
export const filterLimitVendorsToContactForRequest = (
  vendors: OrganizationDataRecipientDto[],
  request: DataRequestDetail,
  dataMap: DataMapDto,
  organization: Organization,
) => {
  return (
    (vendors || [])
      .filter((v) => {
        return v.dataRecipientType !== "storage_location";
      })
      // TrueVault talks to the Connected System through its automation, so it is never emailed (POL-7958).
      .filter((v) => !isConnectedSystemVendor(v))
      .filter((v) => {
        const limitInstruction = v.instructions.find((vi) => vi.requestType == "LIMIT");
        return limitInstruction?.doNotContact !== true;
      })
      .filter(
        (v) =>
          !v.isInstalled ||
          (request.dataRequestType.regulationLaw.slug === "GDPR" &&
            v.gdprProcessorSetting === "Processor"),
      )
      .filter(
        (v) =>
          isRequestContextApplicableWithCrunchFiltering(
            dataMap,
            request.context,
            v,
            request,
            organization,
          ) && isRequestRegionApplicable(request, v),
      )
  );
};

export const alreadyProcessedVendorForLimitRequest = (
  vendor: OrganizationDataRecipientDto,
  request: DataRequestDetail,
): boolean => {
  return (
    (request?.vendorOutcomes || []).find(
      (outcome) => outcome.vendor.id === vendor.vendorId && outcome.processed,
    ) !== undefined
  );
};

const getVendorsToContactForLimitRequest = (
  allVendors: OrganizationDataRecipientDto[],
  request: DataRequestDetail,
  dataMap: DataMapDto,
  answers: SurveyAnswers,
  organization: Organization,
  bizSurveyAnswers?: SurveyAnswers,
): {
  contactVendorsList: OrganizationDataRecipientDto[];
  noSendVendorList: OrganizationDataRecipientDto[];
  sendVendorList: OrganizationDataRecipientDto[];
  completedVendorList: OrganizationDataRecipientDto[];
  sensitiveCollectedPICs: CategoryDto[];
} => {
  const isEmployee = request.context == "EMPLOYEE";

  const oldLimitVendors = filterLimitVendors(allVendors || [], dataMap, answers, request.context);
  const collectedPICIds = getCollectedPicsFromCollectionGroupsWithSensitiveInferencesPIC(
    dataMap,
    isEmployee,
  );
  const oldSensitiveCollectedPICs = filterSensitivePICs(dataMap, collectedPICIds);

  const {
    shouldUseLegacyLimitVendors,
    vendorIdsForProcessingLimitRequests,
    processedSensitivePICIds,
  } = getAllInformationForLimitRequests(allVendors, dataMap);

  // Check if children under 16 data collection is enabled
  const childrenUnder16DataCollectionEnabled =
    bizSurveyAnswers?.[KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG] === "true";

  // Get vendor IDs with non-employment disclosures for children under 16 case
  const vendorIdsWithNonEmploymentDisclosures = new Set<string>();
  dataMap?.disclosure?.forEach((disclosure) => {
    const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
    if (collectionGroup && collectionGroup.type !== "EMPLOYMENT") {
      vendorIdsWithNonEmploymentDisclosures.add(disclosure.vendorId);
    }
  });

  // Include vendors from PIC-based limit logic, plus (if children under 16) vendors with non-employment disclosures that answered "Yes"
  let newLimitVendors = (allVendors || []).filter((v) => {
    const isFromPicBasedLimit = (vendorIdsForProcessingLimitRequests || []).includes(v.vendorId);
    const isFromChildrenUnder16 =
      childrenUnder16DataCollectionEnabled &&
      vendorIdsWithNonEmploymentDisclosures.has(v.vendorId) &&
      v.spiUsedForNonEssentialPurposes === true;
    return isFromPicBasedLimit || isFromChildrenUnder16;
  });
  let newVendorSensitivePICs = dataMap
    ? processedSensitivePICIds.map((id) => dataMap.categories[id])
    : [];

  const shouldUseNewLimitCriteria =
    allVendors && allVendors.length > 0 && dataMap && !shouldUseLegacyLimitVendors;

  const limitVendors = shouldUseNewLimitCriteria ? newLimitVendors : oldLimitVendors;
  const sensitiveCollectedPICs = shouldUseNewLimitCriteria
    ? newVendorSensitivePICs
    : oldSensitiveCollectedPICs;

  const filteredVendors = filterLimitVendorsToContactForRequest(
    limitVendors,
    request,
    dataMap,
    organization,
  );

  const contactVendorsList = sortVendorsByCategory(filteredVendors);

  // contactVendorsList is now partitioned into three lists:
  //  noSendVendorList: vendors that are missing an email address
  //  sendVendorList: vendors that have an email but haven't been processed yet
  //  completedVendorList: vendors that have an email and have been processed

  const noSendVendorList = contactVendorsList.filter((v) => !v.email && !v.defaultEmail) || [];

  const sendVendorList = contactVendorsList.filter(
    (v) => (v.email || v.defaultEmail) && !alreadyProcessedVendorForLimitRequest(v, request),
  );

  const completedVendorList =
    contactVendorsList.filter(
      (v) => (v.email || v.defaultEmail) && alreadyProcessedVendorForLimitRequest(v, request),
    ) || [];

  return {
    contactVendorsList,
    noSendVendorList,
    sendVendorList,
    completedVendorList,
    sensitiveCollectedPICs,
  };
};

export const ContactVendors = () => {
  const [org] = usePrimaryOrganization();
  const {
    request,
    refreshRequest,
    setStepCompleted,
    vendors: { all: vendors },
  } = useDataRequestProcessingContext();

  const [template] = useContactVendorsMessageTemplate(org.id, request.id);

  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [removedRecipientIds, setRemovedRecipientIds] = useState(new Set<string>());

  const closeModal = () => setNotificationModalOpen(false);

  const [updateVendorOutcome, updateVendorOutcomeRequest] = useApiTrigger(
    (values: ContactVendorRequest) => Api.dataRequest.contactVendors(org.id, request.id, values),
    refreshRequest,
    RequestsError.TransitionProcessing,
  );

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { answers: bizSurveyAnswers } = useSurvey(org.id, BUSINESS_SURVEY_NAME);
  const { data: dataMap } = useDataMap(org.id);

  const { contactVendorsList, noSendVendorList, sendVendorList, completedVendorList } =
    getVendorsToContactForLimitRequest(vendors, request, dataMap, answers, org, bizSurveyAnswers);

  const vendorsContacted = completedVendorList.length > 0;

  const hasValidRecipients = sendVendorList.some((v) => !removedRecipientIds.has(v.vendorId));

  const checkStepCompleted = useCallback(() => {
    // We need to calculate these again since the outer values of the same variables might not be up to date
    const currentVendorsToContact = getVendorsToContactForLimitRequest(
      vendors,
      request,
      dataMap,
      answers,
      org,
      bizSurveyAnswers,
    );
    let numToContact = currentVendorsToContact.contactVendorsList.length;
    let numMissingEmail = currentVendorsToContact.noSendVendorList.length;
    // let numToSend = currentVendorsToContact.sendVendorList.length;
    let numCompleted = currentVendorsToContact.completedVendorList.length;

    let isCompleted = false;

    // If no vendors to contact, then step is complete
    if (numToContact == 0) {
      isCompleted = true;
    }

    // If any vendors have been completed (i.e. the email request has been sent), then step is complete
    // Note: we explicitly don't check that `numCompleted == numToContact` because some vendors may have been removed from when the request was previously sent
    if (numCompleted > 0) {
      isCompleted = true;
    }

    // If all vendors to contact have been removed, and there are no vendors missing an email address, then step is complete
    let allSendVendorsRemoved =
      currentVendorsToContact.sendVendorList.filter((v) => !removedRecipientIds.has(v.vendorId))
        .length == 0;
    if (allSendVendorsRemoved && numMissingEmail == 0) {
      isCompleted = true;
    }

    setStepCompleted(isCompleted);
  }, [
    vendors,
    dataMap,
    request,
    answers,
    bizSurveyAnswers,
    removedRecipientIds,
    setStepCompleted,
    org,
  ]);

  useEffect(() => {
    if (vendors && vendors.length > 0 && request && !isEmptyDataMap(dataMap) && answers) {
      checkStepCompleted();
    }
  }, [vendors, dataMap, request, answers, checkStepCompleted, removedRecipientIds]);

  if (!vendors || isEmptyDataMap(dataMap) || !request || !answers) return null;

  const dispatchEmail = async () => {
    const values = {
      vendors: sendVendorList.map((v) => v.vendorId).filter((id) => !removedRecipientIds.has(id)),
    };
    await updateVendorOutcome(values);
    checkStepCompleted();
  };

  return (
    <>
      {contactVendorsList.length > 0 && (
        <div className="text-t3 mb-std">
          To fulfill your legal obligation, the message below will be emailed to your Data
          Recipients.
        </div>
      )}

      {completedVendorList.length > 0 && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            <p>
              Message successfully sent to{" "}
              <strong>{completedVendorList.map((v) => v.name).join(", ")}.</strong>
            </p>
          </Callout>
        </div>
      )}

      {sendVendorList.length > 0 && (
        <>
          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To:</strong>{" "}
                {vendorsContacted ? (
                  <Tooltip title="You have already contacted vendors for this request">
                    <span className="color-neutral-600 text-decoration-underline">
                      See Recipients
                    </span>
                  </Tooltip>
                ) : (
                  <Link to="#" onClick={() => setNotificationModalOpen(true)}>
                    See Recipients
                  </Link>
                )}
              </p>
              <p>
                <strong>
                  Subject:{" "}
                  {request.dataRequestType.regulationLaw.slug === "VOLUNTARY"
                    ? ""
                    : request.dataRequestType.regulationLaw.abbreviation}{" "}
                  {request.dataRequestType.label}
                </strong>
              </p>

              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>

          <EmailButton
            color="primary"
            variant="contained"
            className="btn-md mb-std"
            loading={updateVendorOutcomeRequest.running}
            onClick={dispatchEmail}
            disabled={noSendVendorList?.length > 0 || !hasValidRecipients || vendorsContacted}
            disabledTooltip={
              vendorsContacted
                ? "You have already contacted vendors for this request"
                : hasValidRecipients
                ? noSendVendorList?.length > 0
                  ? "There are data recipients with no email address associated"
                  : undefined
                : "You must contact at least one vendor"
            }
          >
            Send
          </EmailButton>
        </>
      )}

      {noSendVendorList.length > 0 &&
        (noSendVendorList.length != contactVendorsList.length ? (
          <div className="w-896">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent to the following Data Recipients because there is no
                email address added for them:{" "}
                <strong>{noSendVendorList.map((v) => v.name).join(", ")}.</strong>
              </p>
            </Callout>
          </div>
        ) : (
          <div className="w-896">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent because none of your Data Recipients can be
                contacted. Add email addresses for the following Data Recipients in{" "}
                <Link to={Routes.organization.requestHandling.requestToLimit}>
                  Request Instructions
                </Link>
                , then return to this step to preview and send the notification:{" "}
                <strong>{noSendVendorList.map((v) => v.name).join(", ")}.</strong>
              </p>
            </Callout>
          </div>
        ))}

      {contactVendorsList.length == 0 && (
        <div>
          <Callout variant={CalloutVariant.Yellow}>
            <p>You do not need to contact any vendors in this step.</p>
          </Callout>
        </div>
      )}

      <VendorEmailDisplayDialog
        open={notificationModalOpen}
        onClose={closeModal}
        vendors={sendVendorList}
        allowToggle={true}
        isRecipientRemoved={(v) => removedRecipientIds.has(v.vendorId)}
        removeRecipient={(v) => {
          setRemovedRecipientIds(new Set([...removedRecipientIds, v.vendorId]));
          checkStepCompleted();
        }}
        addRecipient={(v) => {
          setRemovedRecipientIds(
            new Set([...removedRecipientIds].filter((id) => id !== v.vendorId)),
          );
          checkStepCompleted();
        }}
      />
    </>
  );
};
